import { createContext, useCallback, useContext, useState } from 'react';
import { ContentRecommendation } from '@/components/content/ContentRecommendation';
import { EventsContext } from '@/data/context/events';
import { useSettings } from '@/data/Settings';
import { useNextRouter } from '@/data/Content/_NextRouter';

const KEY_LOCAL_STORAGE_COOKIE_DIALOG_ACCEPTED = 'cookieDialogAccepted';

const ssrLocalStorage = () => {
	if (typeof window === 'undefined') {
		return undefined;
	}

	return localStorage;
};

const useCookieDialog = () => {
	const { onCookieDialogAccept } = useContext(EventsContext);
	const router = useNextRouter();
	const { settings } = useSettings();

	const [isCookieDialogAccepted, setIsCookieDialogAccepted] = useState(
		ssrLocalStorage()?.getItem(KEY_LOCAL_STORAGE_COOKIE_DIALOG_ACCEPTED) === 'true'
	);

	const handleCookieDialogAccept = useCallback(
		(redirectUrl?: string) => () => {
			ssrLocalStorage()?.setItem(KEY_LOCAL_STORAGE_COOKIE_DIALOG_ACCEPTED, 'true');
			setIsCookieDialogAccepted(true);
			onCookieDialogAccept({
				gtm: {
					ad_storage: 'granted',
					analytics_storage: 'granted',
					ad_user_data: 'granted',
					ad_personalization: 'granted',
					settings,
				},
			});

			if (redirectUrl) {
				router.push(redirectUrl);
			}
		},
		[onCookieDialogAccept, router, settings]
	);

	return {
		isCookieDialogAccepted,
		handleCookieDialogAccept,
	};
};

const CookieDialogContext = createContext<ReturnType<typeof useCookieDialog> | null>(null);

export const CookieDialogProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const value = useCookieDialog();

	return <CookieDialogContext.Provider value={value}>{children}</CookieDialogContext.Provider>;
};

export const useCookieDialogContext = () => {
	const context = useContext(CookieDialogContext);

	if (!context) {
		throw new Error('useCookieDialog must be used within a CookieDialogProvider');
	}

	return context;
};

const emsName = 'DPV2_Cookies_Dialog';

const NullElement = () => null;

export const DpCookieDialogRecommendation = () => (
	<CookieDialogProvider>
		<ContentRecommendation
			id={emsName}
			properties={{
				emsName,
			}}
			withoutFrame
			loadingElement={<NullElement />}
			emptyElement={<NullElement />}
		/>
	</CookieDialogProvider>
);
